import { capitalizeFirstLetter } from './strings'

export enum FileErrorType {
  SIZE = 'size',
  EXT = 'ext'
}

export type FileError = {
  type: FileErrorType
  message: string
}

export enum BaseErrorType {
  MIN = 'min',
  MAX = 'max',
  REQUIRED = 'required'
}

export type BaseError = {
  type: BaseErrorType
  message: string
}

export interface FileField {
  value?: File
  errors?: FileError[]
}

export interface TextField {
  value?: string
  error?: BaseError
}

export interface NumberField {
  value?: number
  error?: BaseError
}

export interface ArrayField<T> {
  value: T[]
  error?: BaseError
}

export interface ErrorOption {
  message?: string
}

export interface MinMaxErrorOption extends ErrorOption {
  value: number
}

export const resolveStringError = (
  fieldName: string,
  value: string,
  options: { min?: MinMaxErrorOption; max?: MinMaxErrorOption; required?: ErrorOption }
): BaseError | null => {
  if (!value && options.required) {
    return {
      type: BaseErrorType.REQUIRED,
      message: options.required.message || `${capitalizeFirstLetter(fieldName)} is required`
    }
  }

  if (value.length < options.min?.value) {
    return {
      type: BaseErrorType.MIN,
      message:
        options.min.message ||
        `${capitalizeFirstLetter(fieldName)} must be at least ${options.min.value} characters`
    }
  }

  if (value.length > options.max?.value) {
    return {
      type: BaseErrorType.MAX,
      message:
        options.max.message ||
        `${capitalizeFirstLetter(fieldName)} cannot exceed ${options.max.value} characters`
    }
  }

  return null
}

export const resolveNumberError = (
  fieldName: string,
  value: number,
  options: { min?: MinMaxErrorOption; max?: MinMaxErrorOption; required?: ErrorOption }
): BaseError | null => {
  if (!value && value !== 0 && options.required) {
    return {
      type: BaseErrorType.REQUIRED,
      message: options.required.message || `${capitalizeFirstLetter(fieldName)} is required`
    }
  }

  if (value < options.min?.value) {
    return {
      type: BaseErrorType.MIN,
      message:
        options.min.message ||
        `${capitalizeFirstLetter(fieldName)} must be at least ${options.min.value}`
    }
  }

  if (value > options.max?.value) {
    return {
      type: BaseErrorType.MAX,
      message:
        options.max.message ||
        `${capitalizeFirstLetter(fieldName)} cannot exceed ${options.max.value}`
    }
  }

  return null
}

export const resolveArrayError = <T>(
  fieldName: string,
  value: T[],
  options: { min?: MinMaxErrorOption; max?: MinMaxErrorOption; required?: ErrorOption }
) => {
  if (!value.length && options.required) {
    return {
      type: BaseErrorType.REQUIRED,
      message: options.required.message || `${capitalizeFirstLetter(fieldName)} is required`
    }
  }

  if (value.length < options.min?.value) {
    return {
      type: BaseErrorType.MIN,
      message:
        options.min.message ||
        `${capitalizeFirstLetter(fieldName)} must be at least ${options.min.value} items long`
    }
  }

  if (value.length > options.max?.value) {
    return {
      type: BaseErrorType.MAX,
      message:
        options.max.message ||
        `${capitalizeFirstLetter(fieldName)} cannot exceed ${options.max.value} items long`
    }
  }

  return null
}
